import useUniqueId from 'hooks/use-unique-id'
import { getValueForDisplay } from 'lib/rich'
import React from 'react'

// Static SVG definitions
function svgDefs(dimArrowId) {
  return (
    <defs>
      <marker
        id={dimArrowId}
        markerWidth={6}
        markerHeight={6}
        refX={6}
        refY={3}
        orient="auto-start-reverse"
        markerUnits="strokeWidth">
        <path d="M0,0 L6,3 L0,6 Z" fill="#000" />
      </marker>
    </defs>
  )
}

export type Segment = {
  height?: number
  upperDiameter?: number
  upperRoundingRadius?: number
  lowerDiameter?: number
  lowerRoundingRadius?: number
  group?: string
  fillColor?: string // overrides default group color
}

/** Compute cumulative heights */
const getCumHeights = (segs: Segment[]): number[] => {
  const arr: number[] = []
  let sum = 0
  for (let i = 0; i < segs.length; i++) {
    arr.push(sum)
    sum += segs[i].height!
  }
  arr.push(sum)
  return arr
}

/** Total height */
const totalHeight = (segs: Segment[]): number =>
  segs.reduce((acc, s) => acc + s.height!, 0)

/** Maximum radius */
const maxRadius = (segs: Segment[]): number =>
  segs.reduce(
    (r, s) => Math.max(r, s.lowerDiameter! / 2, s.upperDiameter! / 2),
    0,
  )

export type Point = { x: number; y: number }

/**
 * Compute the two tangent points [inner, outer] for a fillet at a corner.
 * @param p   The raw corner point (e.g. top-right or bottom-right).
 * @param d1  First flat edge direction (unit vector pointing inward).
 * @param d2  Second edge direction (unit vector pointing inward).
 * @param r   Rounding radius. If <=0, returns [p, p].
 */
export function computeFillet(
  p: Point,
  d1: Point,
  d2: Point,
  r: number,
): [Point, Point] {
  // treat zero as no fillet, but allow negative to invert
  if (r === 0) {
    return [{ ...p }, { ...p }]
  }
  const absR = Math.abs(r)

  const ang = d1.x * d2.x + d1.y * d2.y
  const dot = Math.acos(Math.min(1, Math.max(-1, ang))) // clamp (just in case of tiny floating error)
  // distance along each edge to the tangent
  const tanHalf = Math.tan(dot / 2)
  // edge-tangent length
  const t = absR / tanHalf

  return [
    { x: p.x + d1.x * t, y: p.y + d1.y * t },
    { x: p.x + d2.x * t, y: p.y + d2.y * t },
  ]
}

/**
 * Returns [p0, p1, p2, p3] from top to bottom along the right side.
 * @param p0   The top corner point.
 * @param r0   Rounding radius at the top corner.
 * @param p1   The bottom corner point.
 * @param r1   Rounding radius at the bottom corner.
 */
export function getRightSidePoints(
  p0: Point,
  r0: number,
  p1: Point,
  r1: number,
): [Point, Point, Point, Point] {
  // vector from p0 → p1, and its length
  const dx = p1.x - p0.x
  const dy = p1.y - p0.y
  const len = Math.hypot(dx, dy)
  const dirDown = len > 0 ? { x: dx / len, y: dy / len } : { x: 0, y: 0 }
  const dirUp = { x: -dirDown.x, y: -dirDown.y }

  // top fillet: flat top edge goes left (−1,0), side goes down
  const [topOuter, topInner] = computeFillet(p0, { x: -1, y: 0 }, dirDown, r0)

  // bottom fillet: flat bottom edge goes left (−1,0), side goes up
  const [botOuter, botInner] = computeFillet(p1, { x: -1, y: 0 }, dirUp, r1)

  // Return in order: [topOuter, topInner, bottomInner, bottomOuter]
  return [topOuter, topInner, botInner, botOuter]
}

/**
 * Build an SVG "d" path for one vertical side (left or right),
 * drawing either top→bottom or bottom→top.
 *
 * @param p0          The “start” corner point.
 * @param r0          Fillet radius at p0.
 * @param p1          The “end” corner point.
 * @param r1          Fillet radius at p1.
 * @param leftSide    If true, draws the left side (flat edges point +X); otherwise right side (flat edges point −X).
 * @param bottomUp    If true, draws from p1→p0; otherwise p0→p1.
 * @returns           A string you can drop into `<path d="M..."/>`.
 */
export function getSidePath(
  p0: Point,
  r0: number,
  p1: Point,
  r1: number,
  leftSide: boolean,
  bottomUp: boolean,
): string {
  // 1) optionally swap start/end so we always compute in the draw order
  if (bottomUp) {
    ;[p0, p1] = [p1, p0]
    ;[r0, r1] = [r1, r0]
  }
  if (leftSide) {
    p0.x = -p0.x
    p1.x = -p1.x
  }

  // 2) compute normalized side direction
  const dx = p1.x - p0.x
  const dy = p1.y - p0.y
  const len = Math.hypot(dx, dy)
  const sideDirDown = len > 0 ? { x: dx / len, y: dy / len } : { x: 0, y: 0 }
  const sideDirUp = { x: -sideDirDown.x, y: -sideDirDown.y }

  // 3) flat‐edge direction: leftSide? +X : −X
  const flatDir: Point = leftSide ? { x: 1, y: 0 } : { x: -1, y: 0 }

  // 4) tangent points at start/end
  const [outer0, inner0] = computeFillet(p0, flatDir, sideDirDown, r0)
  const [outer1, inner1] = computeFillet(p1, flatDir, sideDirUp, r1)

  // 5) arc‐sweep flags via cross(flat, side)
  const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x
  let sweep0 = cross(flatDir, sideDirDown) > 0 ? 0 : 1
  if (r0 < 0) {
    sweep0 = sweep0 === 0 ? 1 : 0
  }
  let sweep1 = cross(flatDir, sideDirUp) > 0 ? 1 : 0
  if (r1 < 0) {
    sweep1 = sweep1 === 0 ? 1 : 0
  }

  // 6) build the path
  let d = ` ${outer0.x} ${outer0.y}`

  // start corner
  if (r0 !== 0) {
    d += ` A ${r0} ${Math.abs(r0)} 0 0 ${sweep0} ${inner0.x} ${inner0.y}`
  } else {
    d += ` L ${inner0.x} ${inner0.y}`
  }

  // straight segment
  d += ` L ${inner1.x} ${inner1.y}`

  // end corner
  if (r1 !== 0) {
    d += ` A ${r1} ${Math.abs(r1)} 0 0 ${sweep1} ${outer1.x} ${outer1.y}`
  } else {
    d += ` L ${outer1.x} ${outer1.y}`
  }

  return d
}

export type SegmentsViewerProps = {
  segments: Segment[]
  highlightedIndex?: number
  width?: number // svg width in px
  height?: number // svg height in px
  margin?: number // margin in px around shape
  defaultGroup?: string // group to show by default
  zoomGroup?: string // group to zoom to on load
  groupColors?: Record<string, string> // colors for each group
  metric: boolean
  showGrid?: boolean
  minor?: number // grid spacing in world units
  major?: number // major grid spacing in world units
  rotate?: boolean
}

function clamp(value, min = 0, max = 1000) {
  if (typeof value !== 'number') {
    return min
  }
  if (value < min) {
    return min
  }
  if (value > max) {
    return min
  }
  return value
}

export const SegmentsViewer = (props: SegmentsViewerProps) => {
  const {
    segments: inputSegments,
    highlightedIndex = -1,
    width = 400,
    height = 600,
    metric = true,
    margin = 20,
    showGrid = false,
    minor = 10,
    major = 10,
    rotate = true,
    defaultGroup = 'holder',
    zoomGroup = undefined,
    groupColors = {
      holder: '#83c5be',
      tool: '#cfdde4',
    },
  } = props

  const dimArrowId = useUniqueId('dim-arrow')

  if (!inputSegments || !inputSegments?.length) {
    return (
      <svg
        width={width}
        height={height}
        style={{
          background: '#fafafa',
          display: 'block',
          width: '100%',
          height: '100%',
        }}
        xmlns="http://www.w3.org/2000/svg"></svg>
    )
  }

  // sanitize inputs
  const segments = inputSegments.map((s, i) => ({
    height: clamp(s.height),
    upperDiameter: clamp(s.upperDiameter),
    lowerDiameter: clamp(s.lowerDiameter),
    group: s.group || defaultGroup,
    fillColor:
      i == highlightedIndex
        ? '#e0d79b'
        : s.fillColor || groupColors[s.group || defaultGroup] || '#444',
    upperRoundingRadius: s.upperRoundingRadius || 0,
    lowerRoundingRadius: s.lowerRoundingRadius || 0,
  }))

  const outlineColor = 'rgba(128,128,128, .95)'

  // compute metrics
  const cumHeights = segments.length ? getCumHeights(segments) : []
  const totalH = totalHeight(segments)
  const maxR = maxRadius(segments)
  const N = segments.length

  // compute height span for each group
  const groupNames = Array.from(new Set(segments.map((s) => s.group)))
  const groupHeights: Record<string, { start: number; end: number }> = {}

  groupNames.forEach((name) => {
    const startIdx = segments.findIndex((s) => s.group === name)
    const endIdx = segments.map((s) => s.group).lastIndexOf(name) + 1
    const startY = cumHeights[startIdx]
    const endY = cumHeights[endIdx]
    groupHeights[name] = { start: startY, end: endY }
  })

  // compute max radius for the zoomGroup only
  const zoomRValues = zoomGroup
    ? segments
        .filter((s) => s.group === zoomGroup)
        .map((s) => Math.max(s.upperDiameter! / 2, s.lowerDiameter! / 2))
    : []

  const zoomR = zoomRValues.length ? Math.max(...zoomRValues) : maxR
  const zoomGroupHeights = groupHeights[zoomGroup]

  const zoomOffset = zoomGroup ? zoomGroupHeights.start : 0
  const zoomH = zoomGroup
    ? zoomGroupHeights.end - zoomGroupHeights.start
    : totalH

  // pixel margin
  const marginPx = margin
  // world extents
  const worldW = rotate ? zoomH : 2 * zoomR
  const worldH = rotate ? 2 * zoomR : zoomH

  // scale world units -> pixels
  const scale = Math.min(
    (width - 2 * marginPx) / worldW,
    (height - 2 * marginPx) / worldH,
  )

  // project world->px
  const px = (wx: number) => wx * scale
  const py = (wy: number) => wy * scale

  // grid lines: cover entire viewport, ensuring they go through origin
  const minorLines: JSX.Element[] = [] // drawn behind major
  const majorLines: JSX.Element[] = []
  if (showGrid) {
    // vertical lines
    for (let gx = -width / 2; gx <= width / 2; gx += minor) {
      const x = px(gx)
      const isMajor = Math.abs(gx % major) < 1e-6
      ;(isMajor ? majorLines : minorLines).push(
        <line
          key={`gx${gx}`}
          x1={x}
          y1={py(-major * 2)}
          x2={x}
          y2={height - py(-major * 2)}
          stroke={isMajor ? '#bbb' : '#eee'}
          strokeWidth={1}
        />,
      )
    }

    // horizontal lines
    for (let gy = -major * 2; gy <= totalH; gy += minor) {
      const y = py(gy)
      const isMajor = Math.abs(gy % major) < 1e-6
      ;(isMajor ? majorLines : minorLines).push(
        <line
          key={`gy${gy}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke={isMajor ? '#bbb' : '#eee'}
          strokeWidth={1}
        />,
      )
    }
  }

  // Build outline path
  const getOutlinePath = (segments) => {
    let outlinePathD = ''
    let startSym = 'M'
    // right side, bottom to top
    segments.forEach((s, i) => {
      const y0 = cumHeights[i]
      const y1 = cumHeights[i + 1]
      const p0 = { x: px(s.upperDiameter! / 2), y: py(y0) }
      const p1 = { x: px(s.lowerDiameter! / 2), y: py(y1) }
      const r0 = px(s.upperRoundingRadius)
      const r1 = px(s.lowerRoundingRadius)
      outlinePathD += startSym + getSidePath(p0, r0, p1, r1, false, false) + ' '
      startSym = 'L'
    })

    // left side, top to bottom
    segments
      .slice()
      .reverse()
      .forEach((s, idx) => {
        const i = segments.length - 1 - idx
        const p0 = { x: px(s.upperDiameter! / 2), y: py(cumHeights[i]) }
        const p1 = { x: px(s.lowerDiameter! / 2), y: py(cumHeights[i + 1]) }
        const r0 = px(s.upperRoundingRadius)
        const r1 = px(s.lowerRoundingRadius)
        outlinePathD += startSym + getSidePath(p0, r0, p1, r1, true, true) + ' '
      })
    outlinePathD += 'Z'
    return outlinePathD
  }

  const segmentsOutline = (
    <path
      d={getOutlinePath(segments)}
      fill="none"
      strokeLinecap="butt"
      strokeLinejoin="bevel"
      stroke={outlineColor}
      strokeWidth={1}
    />
  )

  // segment boundary lines
  const segLines = cumHeights.map((y, i) => {
    const r =
      i === 0
        ? segments[0].upperDiameter! / 2
        : i === N
        ? 0 // last segment
        : segments[i - 1].lowerDiameter! / 2 -
          segments[i - 1].lowerRoundingRadius
    return (
      <line
        key={i}
        x1={px(-r)}
        y1={py(y)}
        x2={px(r)}
        y2={py(y)}
        stroke="#0005"
        strokeWidth={1}
      />
    )
  })

  // centerline
  const centerline = (
    <line
      x1={0}
      y1={-marginPx}
      x2={0}
      y2={py(totalH) + marginPx * 2}
      stroke="#0007"
      strokeWidth={1}
      strokeDasharray="25,5,5,5"
    />
  )

  // draw each segment’s fill as its own rounded trapezoid
  const segmentsFill = segments.map((s, i) => {
    let d = ''
    const y0 = cumHeights[i]
    const y1 = cumHeights[i + 1]
    const p0 = { x: px(s.upperDiameter! / 2), y: py(y0) }
    const p1 = { x: px(s.lowerDiameter! / 2), y: py(y1) }
    const r0 = px(s.upperRoundingRadius)
    const r1 = px(s.lowerRoundingRadius)
    d += 'M' + getSidePath(p0, r0, p1, r1, false, false) + ' '
    d += 'L' + getSidePath(p0, r0, p1, r1, true, true) + ' '
    d += 'Z'

    return (
      <path
        key={i}
        d={d}
        fill={s.fillColor}
        strokeLinecap="butt"
        strokeLinejoin="bevel"
        stroke={s.fillColor}
        strokeWidth={1}
      />
    )
  })

  const DIM_OFFSET = 8 - marginPx
  const xDimY = zoomGroup === 'tool' ? px(totalH) - DIM_OFFSET : +DIM_OFFSET
  const yDimX = -px(zoomR) + DIM_OFFSET

  return (
    <svg
      width={width}
      height={height}
      viewBox={
        rotate
          ? `${px(zoomOffset) - marginPx} ${-height / 2} ${width} ${height}`
          : `${-width / 2} ${-marginPx} ${width} ${height}`
      }
      style={{
        background: '#fafafa',
        display: 'block',
        width: '100%',
        height: '100%',
      }}
      xmlns="http://www.w3.org/2000/svg">
      <g transform={rotate ? `rotate(-90)` : undefined}>
        {svgDefs(dimArrowId)}

        {minorLines}
        {majorLines}

        {segmentsFill}

        {centerline}
        {segLines}
        {segmentsOutline}

        {/* dimension lines for each group’s height */}
        {Object.entries(groupHeights).map(([name, { start, end }]) => {
          return !zoomGroup || name === zoomGroup ? (
            <DimLine
              key={`dim-height-${name}`}
              x1={yDimX}
              y1={py(start)}
              x2={yDimX}
              y2={py(end)}
              value={end - start}
              metric={metric}
              dimArrowId={dimArrowId}
            />
          ) : (
            <></>
          )
        })}

        {/* dimension for zoomGroup max radius */}
        <DimLine
          x1={px(-zoomR)}
          y1={xDimY}
          x2={px(zoomR)}
          y2={xDimY}
          value={2 * zoomR}
          metric={metric}
          dimArrowId={dimArrowId}
        />
      </g>
    </svg>
  )
}

/** Dimension line in pixel coords */
const DimLine: React.FC<{
  x1: number
  y1: number
  x2: number
  y2: number
  value: number
  metric: boolean
  dimArrowId?: string
}> = ({ x1, y1, x2, y2, value, metric, dimArrowId }) => {
  const label = getValueForDisplay(value, 'length', metric)

  // styling
  const fontSizePx = 12

  const isHorizontal = Math.abs(y1 - y2) < 1e-6
  const textX = (x1 + x2) / 2
  const textY = (y1 + y2) / 2
  return (
    <>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#000"
        strokeWidth={1}
        strokeLinecap="butt"
        markerStart={`url(#${dimArrowId})`}
        markerEnd={`url(#${dimArrowId})`}
      />
      <text
        x={textX}
        y={isHorizontal ? y1 : textY}
        transform={!isHorizontal ? `rotate(90, ${textX}, ${textY})` : undefined}
        fontSize={`${fontSizePx}px`}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#000"
        stroke="#fff"
        strokeWidth={6}
        paintOrder="stroke">
        {label}
      </text>
    </>
  )
}
